import { orderBy } from 'firebase/firestore';
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { MOCK_LISTINGS } from '../data/listings.data';
import { getListingsTranslations } from '../constants/listings.translations';
import { PRICE_RANGES } from '../constants/location.constants';
import { Search, Heart, MapPin, Camera, Loader2, ChevronDown, Check, X, ArrowLeft, Clock } from 'lucide-react';
import { SiteHeader } from '@/components/layout/SiteHeader';
import { ScrollToTop } from '@/components/ui/scroll-to-top';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useFavorites } from '@/hooks/useFavorites';
import { LocationAutocomplete } from '@/components/listings/LocationAutocomplete';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';

interface LocationFilter {
  country: string;
  stateCode: string;
  city: string;
}

export default function Listings() {
  const { language, setLanguage } = useLanguage();
  const t = getListingsTranslations(language);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isFavorite, toggleFavorite } = useFavorites();
  const [pendingDealIds, setPendingDealIds] = useState<Set<string>>(new Set());
const [realListings, setRealListings] = useState<any[]>([]);
const [listingsLoading, setListingsLoading] = useState(true);

// Fetch real deals from Firestore
useEffect(() => {
  const fetchRealListings = async () => {
    try {
      setListingsLoading(true);

      // Requête 1 : deals dont l'étape rend publique par défaut,
      // sauf ceux explicitement masqués par le staff.
      const stageSnap = await getDocs(
        query(
          collection(db, 'dealSubmissions'),
          where('currentStage', 'in', [
            'published',
            'lender_submission',
            'nda_signed',
            'buyer_contact',
            'first_meeting',
            'loi_signed',
          ]),
          orderBy('createdAt', 'desc')
        )
      );

      // Requête 2 : deals forcés visibles par le staff, peu importe l'étape.
      const overrideSnap = await getDocs(
        query(
          collection(db, 'dealSubmissions'),
          where('listingOverride', '==', 'show'),
          orderBy('createdAt', 'desc')
        )
      );

      const seen = new Set<string>();
      const rawDocs: any[] = [];

      stageSnap.docs.forEach(d => {
        const data = d.data();
        if (data.listingOverride === 'hide') return; // masqué manuellement
        if (!seen.has(d.id)) { seen.add(d.id); rawDocs.push({ id: d.id, data }); }
      });
      overrideSnap.docs.forEach(d => {
        if (!seen.has(d.id)) { seen.add(d.id); rawDocs.push({ id: d.id, data: d.data() }); }
      });

      const mapped = rawDocs.map(({ id, data: d }) => ({
        id,
        listingNumber: d.referenceNumber || d.reference_number || id,
        title: d.isConfidential
          ? `${d.industry || 'Business'} — ${d.locationCity || 'Location TBD'}`
          : (d.legalName || d.businessName || 'Business'),
        description: d.description || d.title || '',
        category: (d.industry || 'other').toLowerCase().replace(/\s+/g, '_'),
        location: {
          city: d.locationCity || '',
          state: '',
          country: d.locationCountry || 'US',
        },
        financials: {
          price: d.askingPrice || 0,
          currency: 'USD',
          cashFlow: d.cashFlowEquity || 0,
          sde: d.cashFlowEquity || 0,
          ebitda: d.ebitdaYear1 || 0,
          grossRevenue: d.revenueYear1 || 0,
          revenueGrowth3yr: 0,
          hasPositiveCashFlow: (d.ebitdaYear1 || 0) > 0,
        },
        imageUrl: d.imageUrl || 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=500&fit=crop',
        imageCount: 1,
        badges: [],
        preFinancingOffers: 0,
        detailUrl: `/listings/${id}`,
        yearEstablished: d.yearEstablished || null,
        employeeCount: d.fullTimeEmployees || 0,
        employeeCountRange: '',
        status: 'active',
        isConfidential: d.isConfidential ?? true,
        isRealDeal: true,
      }));

      setRealListings(mapped);
    } catch (err) {
      console.error('[Listings] Error fetching real listings:', err);
    } finally {
      setListingsLoading(false);
    }
  };

  fetchRealListings();
}, []);

// Combine real + mock listings (mock shown when no real deals yet)
const allListings = realListings.length > 0
  ? realListings
  : MOCK_LISTINGS;

  // Fetch deals where LOI is fully signed (status = 'signed', document_type = 'loi')
  useEffect(() => {
    const fetchPendingDeals = async () => {
      try {
        const snap = await getDocs(
query(collection(db, 'dealDocuments'), where('document_type', '==', 'loi'), where('status', '==', 'signed'))
);
if (!snap.empty) {
setPendingDealIds(new Set(snap.docs.map(d => d.data().deal_id || d.data().dealId).filter(Boolean)));
}
      } catch { /* silent */ }
    };
    fetchPendingDeals();
  }, []);
  
  // Infinite scroll state
  const ITEMS_PER_PAGE = 9;
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  
  // Pending filter states (user input before applying)
  const [pendingSearchTerm, setPendingSearchTerm] = useState('');
  const [pendingIndustry, setPendingIndustry] = useState('all');
  const [pendingLocationText, setPendingLocationText] = useState('');
  const [pendingLocationFilter, setPendingLocationFilter] = useState<LocationFilter | null>(null);
  const [pendingPriceMin, setPendingPriceMin] = useState('0');
  const [pendingPriceMax, setPendingPriceMax] = useState('none');
  const [pendingStatus, setPendingStatus] = useState('active');
  const [pendingSpecialFilters, setPendingSpecialFilters] = useState<string[]>([]);

  // Applied filter states (used for actual filtering)
  const [searchTerm, setSearchTerm] = useState('');
  const [industry, setIndustry] = useState('all');
  const [locationFilter, setLocationFilter] = useState<LocationFilter | null>(null);
  const [priceMin, setPriceMin] = useState('0');
  const [priceMax, setPriceMax] = useState('none');
  const [status, setStatus] = useState('active');
  const [specialFilters, setSpecialFilters] = useState<string[]>([]);
  
  // Special filter options for multi-select
  const specialFilterOptions = [
    { value: 'seller-financing', labelKey: 'special_seller_financing' },
    { value: 'e2-visa', labelKey: 'special_e2_visa' },
    { value: 'pre-financing', labelKey: 'special_pre_financing' },
    { value: 'absentee-owner', labelKey: 'special_absentee_owner' },
    { value: 'semi-absentee', labelKey: 'special_semi_absentee' },
    { value: 'home-based', labelKey: 'special_home_based' },
  ];

  const getListingTitle = (id: string): string => {
    const key = `listing_${id.split('-')[1]}_title` as keyof typeof t;
    return (t[key] as string) || id;
  };

  const getListingDescription = (id: string): string => {
    const key = `listing_${id.split('-')[1]}_description` as keyof typeof t;
    return (t[key] as string) || '';
  };

  // Apply filters function
  const applyFilters = () => {
    setSearchTerm(pendingSearchTerm);
    setIndustry(pendingIndustry);
    setLocationFilter(pendingLocationFilter);
    setPriceMin(pendingPriceMin);
    setPriceMax(pendingPriceMax);
    setStatus(pendingStatus);
    setSpecialFilters(pendingSpecialFilters);
    setVisibleCount(ITEMS_PER_PAGE);
  };
  
  // Toggle special filter selection
  const toggleSpecialFilter = (filterValue: string) => {
    setPendingSpecialFilters(prev => 
      prev.includes(filterValue)
        ? prev.filter(f => f !== filterValue)
        : [...prev, filterValue]
    );
  };
  
  // Remove applied special filter
  const removeSpecialFilter = (filterValue: string) => {
    setSpecialFilters(prev => prev.filter(f => f !== filterValue));
    setPendingSpecialFilters(prev => prev.filter(f => f !== filterValue));
  };

  // Preview count based on pending filters (real-time)
  const previewFilteredCount = useMemo(() => {
    return allListings.filter((listing) => {
      // Search filter
      if (pendingSearchTerm) {
        const search = pendingSearchTerm.toLowerCase();
        const title = getListingTitle(listing.id).toLowerCase();
        const description = getListingDescription(listing.id).toLowerCase();
        const listingNum = listing.listingNumber.toLowerCase();
        if (!title.includes(search) && !description.includes(search) && !listingNum.includes(search)) {
          return false;
        }
      }

      // Industry filter
      if (pendingIndustry !== 'all' && listing.category !== pendingIndustry) {
        return false;
      }

      // Location filter
      if (pendingLocationFilter) {
        const loc = listing.location;
        if (pendingLocationFilter.country && loc.country !== pendingLocationFilter.country) return false;
        if (pendingLocationFilter.stateCode && loc.state.toUpperCase() !== pendingLocationFilter.stateCode.toUpperCase()) return false;
        if (pendingLocationFilter.city && !loc.city.toLowerCase().includes(pendingLocationFilter.city.toLowerCase())) return false;
      }

      // Price range filter
      const price = listing.financials.price;
      const minPrice = pendingPriceMin === '0' ? 0 : parseInt(pendingPriceMin);
      const maxPrice = pendingPriceMax === 'none' ? Infinity : parseInt(pendingPriceMax);
      if (price < minPrice || price > maxPrice) return false;

      // Special filters
      if (pendingSpecialFilters.length > 0) {
        const badges = listing.badges.map((b) => b.type);
        for (const filter of pendingSpecialFilters) {
          if (filter === 'seller-financing' && !badges.includes('seller_financing')) return false;
          if (filter === 'e2-visa' && !badges.includes('e2_visa')) return false;
          if (filter === 'pre-financing' && listing.preFinancingOffers === 0) return false;
          if (filter === 'absentee-owner' && !badges.includes('absentee_owner')) return false;
          if (filter === 'semi-absentee' && !badges.includes('semi_absentee')) return false;
          if (filter === 'home-based' && !badges.includes('home_based')) return false;
        }
      }

      return true;
    }).length;
  }, [pendingSearchTerm, pendingIndustry, pendingLocationFilter, pendingPriceMin, pendingPriceMax, pendingSpecialFilters, language]);

  // Filter listings based on applied filters
  const filteredListings = useMemo(() => {
    return allListings.filter((listing) => {
      // Search filter (title, description, listing number)
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const title = getListingTitle(listing.id).toLowerCase();
        const description = getListingDescription(listing.id).toLowerCase();
        const listingNum = listing.listingNumber.toLowerCase();
        if (!title.includes(search) && !description.includes(search) && !listingNum.includes(search)) {
          return false;
        }
      }

      // Industry filter
      if (industry !== 'all' && listing.category !== industry) {
        return false;
      }

      // Location filter (from Google Places autocomplete)
      if (locationFilter) {
        const loc = listing.location;
        
        // Filter by country if specified
        if (locationFilter.country && loc.country !== locationFilter.country) {
          return false;
        }
        
        // Filter by state/province if specified
        if (locationFilter.stateCode) {
          if (loc.state.toUpperCase() !== locationFilter.stateCode.toUpperCase()) {
            return false;
          }
        }
        
        // Filter by city if specified
        if (locationFilter.city) {
          if (!loc.city.toLowerCase().includes(locationFilter.city.toLowerCase())) {
            return false;
          }
        }
      }

      // Price range filter (min/max)
      const price = listing.financials.price;
      const minPrice = priceMin === '0' ? 0 : parseInt(priceMin);
      const maxPrice = priceMax === 'none' ? Infinity : parseInt(priceMax);
      
      if (price < minPrice || price > maxPrice) {
        return false;
      }

      // Special filters (multi-select - must match ALL selected filters)
      if (specialFilters.length > 0) {
        const badges = listing.badges.map((b) => b.type);
        for (const filter of specialFilters) {
          if (filter === 'seller-financing' && !badges.includes('seller_financing')) return false;
          if (filter === 'e2-visa' && !badges.includes('e2_visa')) return false;
          if (filter === 'pre-financing' && listing.preFinancingOffers === 0) return false;
          if (filter === 'absentee-owner' && !badges.includes('absentee_owner')) return false;
          if (filter === 'semi-absentee' && !badges.includes('semi_absentee')) return false;
          if (filter === 'home-based' && !badges.includes('home_based')) return false;
        }
      }

      return true;
    });
  }, [searchTerm, industry, locationFilter, priceMin, priceMax, status, specialFilters, language]);

  // Visible listings (paginated)
  const visibleListings = useMemo(() => {
    return filteredListings.slice(0, visibleCount);
  }, [filteredListings, visibleCount]);

  const hasMore = visibleCount < filteredListings.length;

  // Infinite scroll observer
  const loadMore = useCallback(() => {
    if (hasMore && !isLoadingMore) {
      setIsLoadingMore(true);
      setTimeout(() => {
        setVisibleCount((prev) => Math.min(prev + ITEMS_PER_PAGE, filteredListings.length));
        setIsLoadingMore(false);
      }, 300);
    }
  }, [hasMore, isLoadingMore, filteredListings.length]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [loadMore]);

  // Extract slug from detailUrl for favorites consistency with detail page
  const getSlugFromDetailUrl = (detailUrl?: string): string | null => {
    if (!detailUrl) return null;
    const parts = detailUrl.split('/');
    return parts[parts.length - 1] || null;
  };

  const handleToggleFavorite = async (listing: (typeof allListings)[0]) => {
    const slug = getSlugFromDetailUrl(listing.detailUrl);
    if (!slug) return;
    
    const nowFavorite = await toggleFavorite(slug);
    toast({
      description: nowFavorite ? t.added_to_favorites : t.removed_from_favorites,
    });
  };

  const isListingFavorite = (listing: typeof MOCK_LISTINGS[0]): boolean => {
    const slug = getSlugFromDetailUrl(listing.detailUrl);
    return slug ? isFavorite(slug) : false;
  };

  const clearFilters = () => {
    // Clear pending filters
    setPendingSearchTerm('');
    setPendingIndustry('all');
    setPendingLocationText('');
    setPendingLocationFilter(null);
    setPendingPriceMin('0');
    setPendingPriceMax('none');
    setPendingStatus('active');
    setPendingSpecialFilters([]);
    // Clear applied filters
    setSearchTerm('');
    setIndustry('all');
    setLocationFilter(null);
    setPriceMin('0');
    setPriceMax('none');
    setStatus('active');
    setSpecialFilters([]);
    setVisibleCount(ITEMS_PER_PAGE);
  };

  const handleLocationSelect = (location: { country: string; stateCode: string; city: string } | null) => {
    if (location) {
      setPendingLocationFilter({
        country: location.country,
        stateCode: location.stateCode,
        city: location.city,
      });
    } else {
      setPendingLocationFilter(null);
    }
  };

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat(language === 'fr' ? 'fr-CA' : 'en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatCashFlow = (amount: number) => {
    return new Intl.NumberFormat(language === 'fr' ? 'fr-CA' : 'en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getCountryBadge = (country: string) => {
    return country === 'CA' ? '🇨🇦 CANADA' : '🇺🇸 USA';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-300">
      {/* Site Header with hamburger mobile */}
      <SiteHeader
        lang={language}
        onToggleLang={() => setLanguage(language === 'en' ? 'fr' : 'en')}
      />

      {/* Deal Monitor Section */}
      <div className="max-w-[1400px] mx-auto px-5 py-8">
        {/* Back button + Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm font-medium text-[#2c3e50] hover:text-[#b8924f] transition-colors mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            {language === 'fr' ? 'Retour' : 'Back'}
          </button>
          <h1 className="text-3xl md:text-4xl font-semibold text-[#2c3e50] mb-2">
            {t.title}
          </h1>
          <p className="text-[#7f8c8d] text-base">{t.subtitle}</p>
        </div>

        {/* Filter Section */}
        <div className="bg-white p-6 md:p-8 rounded-xl shadow-sm mb-8">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-xl font-semibold text-[#2c3e50]">{t.filter_title}</h2>
          </div>
          <p className="text-sm text-[#7f8c8d] mb-6">{t.filter_subtitle}</p>

          {/* Row 1: Search, Special Filters, Status */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
            {/* Search */}
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-[#2c3e50] mb-2">{t.search}</label>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7f8c8d]" />
                <Input
                  type="text"
                  placeholder={t.search_placeholder}
                  value={pendingSearchTerm}
                  onChange={(e) => setPendingSearchTerm(e.target.value)}
                  className="pl-11 h-11 border-slate-200 focus:border-[#b8924f] focus:ring-[#b8924f]/20"
                />
              </div>
            </div>

            {/* Special Filters - Multi-select */}
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-[#2c3e50] mb-2">{t.special_filters}</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="h-11 justify-between border-slate-200 focus:border-[#b8924f] focus:ring-[#b8924f]/20 bg-white text-left font-normal"
                  >
                    <span className="text-sm">
                      {pendingSpecialFilters.length === 0
                        ? t.special_select_filters
                        : t.special_selected.replace('{count}', pendingSpecialFilters.length.toString())}
                    </span>
                    <ChevronDown className="h-4 w-4 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[250px] p-2 bg-white z-50" align="start">
                  <div className="space-y-1">
                    {specialFilterOptions.map((option) => (
                      <div
                        key={option.value}
                        className="flex items-center space-x-2 p-2 rounded hover:bg-slate-50 cursor-pointer"
                        onClick={() => toggleSpecialFilter(option.value)}
                      >
                        <Checkbox
                          id={option.value}
                          checked={pendingSpecialFilters.includes(option.value)}
                          onCheckedChange={() => toggleSpecialFilter(option.value)}
                        />
                        <label
                          htmlFor={option.value}
                          className="text-sm cursor-pointer flex-1"
                        >
                          {t[option.labelKey as keyof typeof t]}
                        </label>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Status */}
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-[#2c3e50] mb-2">{t.status}</label>
              <Select value={pendingStatus} onValueChange={setPendingStatus}>
                <SelectTrigger className="h-11 border-slate-200 focus:border-[#b8924f] focus:ring-[#b8924f]/20 bg-white">
                  <SelectValue placeholder={t.status_active} />
                </SelectTrigger>
                <SelectContent className="bg-white z-50">
                  <SelectItem value="active">{t.status_active}</SelectItem>
                  <SelectItem value="pending">{t.status_pending}</SelectItem>
                  <SelectItem value="sold">{t.status_sold}</SelectItem>
                  <SelectItem value="all">{t.status_all}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Row 2: Location, Industry */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
            {/* Location */}
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-[#2c3e50] mb-2">{t.location_search}</label>
              <LocationAutocomplete
                value={pendingLocationText}
                onChange={setPendingLocationText}
                onLocationSelect={handleLocationSelect}
                placeholder={t.location_placeholder}
                language={language}
              />
            </div>

            {/* Industry */}
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-[#2c3e50] mb-2">{t.industry}</label>
              <Select value={pendingIndustry} onValueChange={setPendingIndustry}>
                <SelectTrigger className="h-11 border-slate-200 focus:border-[#b8924f] focus:ring-[#b8924f]/20 bg-white">
                  <SelectValue placeholder={t.all_industries} />
                </SelectTrigger>
                <SelectContent className="bg-white z-50">
                  <SelectItem value="all">{t.all_industries}</SelectItem>
                  <SelectItem value="restaurant">{t.industry_restaurant}</SelectItem>
                  <SelectItem value="technology">{t.industry_technology}</SelectItem>
                  <SelectItem value="construction">{t.industry_construction}</SelectItem>
                  <SelectItem value="healthcare">{t.industry_healthcare}</SelectItem>
                  <SelectItem value="ecommerce">{t.industry_ecommerce}</SelectItem>
                  <SelectItem value="manufacturing">{t.industry_manufacturing}</SelectItem>
                  <SelectItem value="agriculture">{t.industry_agriculture}</SelectItem>
                  <SelectItem value="beauty">{t.industry_beauty}</SelectItem>
                  <SelectItem value="retail">{t.industry_retail}</SelectItem>
                  <SelectItem value="education">{t.industry_education}</SelectItem>
                  <SelectItem value="entertainment">{t.industry_entertainment}</SelectItem>
                  <SelectItem value="financial">{t.industry_financial}</SelectItem>
                  <SelectItem value="transport">{t.industry_transport}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Row 3: Min Price, Max Price, Min EBITDA, Max EBITDA, Min Cash Flow, Max Cash Flow */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-5 mb-6">
            {/* Price Min */}
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-[#2c3e50] mb-2">{t.price_min}</label>
              <Select value={pendingPriceMin} onValueChange={setPendingPriceMin}>
                <SelectTrigger className="h-11 border-slate-200 focus:border-[#b8924f] focus:ring-[#b8924f]/20 bg-white">
                  <SelectValue placeholder={t.no_minimum} />
                </SelectTrigger>
                <SelectContent className="bg-white z-50">
                  <SelectItem value="0">{t.no_minimum}</SelectItem>
                  <SelectItem value="500000">{t.less_than_500k}</SelectItem>
                  {PRICE_RANGES.map((range) => (
                    <SelectItem key={range.value} value={range.value}>
                      {language === 'fr' ? range.fr : range.en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Price Max */}
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-[#2c3e50] mb-2">{t.price_max}</label>
              <Select value={pendingPriceMax} onValueChange={setPendingPriceMax}>
                <SelectTrigger className="h-11 border-slate-200 focus:border-[#b8924f] focus:ring-[#b8924f]/20 bg-white">
                  <SelectValue placeholder={t.no_maximum} />
                </SelectTrigger>
                <SelectContent className="bg-white z-50">
                  <SelectItem value="none">{t.no_maximum}</SelectItem>
                  <SelectItem value="500000">{t.less_than_500k}</SelectItem>
                  {PRICE_RANGES.map((range) => (
                    <SelectItem key={range.value} value={range.value}>
                      {language === 'fr' ? range.fr : range.en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Min EBITDA */}
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-[#2c3e50] mb-2">{t.min_ebitda}</label>
              <Select defaultValue="0">
                <SelectTrigger className="h-11 border-slate-200 focus:border-[#b8924f] focus:ring-[#b8924f]/20 bg-white">
                  <SelectValue placeholder={t.no_minimum} />
                </SelectTrigger>
                <SelectContent className="bg-white z-50">
                  <SelectItem value="0">{t.no_minimum}</SelectItem>
                  <SelectItem value="500000">{t.less_than_500k}</SelectItem>
                  {PRICE_RANGES.map((range) => (
                    <SelectItem key={range.value} value={range.value}>
                      {language === 'fr' ? range.fr : range.en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Max EBITDA */}
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-[#2c3e50] mb-2">{t.max_ebitda}</label>
              <Select defaultValue="none">
                <SelectTrigger className="h-11 border-slate-200 focus:border-[#b8924f] focus:ring-[#b8924f]/20 bg-white">
                  <SelectValue placeholder={t.no_maximum} />
                </SelectTrigger>
                <SelectContent className="bg-white z-50">
                  <SelectItem value="none">{t.no_maximum}</SelectItem>
                  <SelectItem value="500000">{t.less_than_500k}</SelectItem>
                  {PRICE_RANGES.map((range) => (
                    <SelectItem key={range.value} value={range.value}>
                      {language === 'fr' ? range.fr : range.en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Min Cash Flow */}
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-[#2c3e50] mb-2">{t.min_cash_flow}</label>
              <Select defaultValue="0">
                <SelectTrigger className="h-11 border-slate-200 focus:border-[#b8924f] focus:ring-[#b8924f]/20 bg-white">
                  <SelectValue placeholder={t.no_minimum} />
                </SelectTrigger>
                <SelectContent className="bg-white z-50">
                  <SelectItem value="0">{t.no_minimum}</SelectItem>
                  <SelectItem value="500000">{t.less_than_500k}</SelectItem>
                  {PRICE_RANGES.map((range) => (
                    <SelectItem key={range.value} value={range.value}>
                      {language === 'fr' ? range.fr : range.en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Max Cash Flow */}
            <div className="flex flex-col">
              <label className="text-sm font-semibold text-[#2c3e50] mb-2">{t.max_cash_flow}</label>
              <Select defaultValue="none">
                <SelectTrigger className="h-11 border-slate-200 focus:border-[#b8924f] focus:ring-[#b8924f]/20 bg-white">
                  <SelectValue placeholder={t.no_maximum} />
                </SelectTrigger>
                <SelectContent className="bg-white z-50">
                  <SelectItem value="none">{t.no_maximum}</SelectItem>
                  <SelectItem value="500000">{t.less_than_500k}</SelectItem>
                  {PRICE_RANGES.map((range) => (
                    <SelectItem key={range.value} value={range.value}>
                      {language === 'fr' ? range.fr : range.en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Active Special Filters Badges */}
          {specialFilters.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-100">
              <span className="text-sm text-[#7f8c8d] mr-2 self-center">{t.active_filters}:</span>
              {specialFilters.map((filter) => {
                const option = specialFilterOptions.find(o => o.value === filter);
                if (!option) return null;
                return (
                  <span
                    key={filter}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#b8924f]/10 text-[#b8924f] text-sm font-medium rounded-full border border-[#b8924f]/20"
                  >
                    {t[option.labelKey as keyof typeof t]}
                    <button
                      onClick={() => removeSpecialFilter(filter)}
                      className="ml-1 hover:bg-[#b8924f]/20 rounded-full p-0.5 transition-colors"
                      aria-label={`Remove ${t[option.labelKey as keyof typeof t]} filter`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}

          <div className="flex items-center justify-between gap-3 mt-4">
            {/* Preview count */}
            <span className="text-sm text-[#7f8c8d] bg-slate-50 px-3 py-2 rounded-lg">
              {t.preview_results.replace('{count}', previewFilteredCount.toString())}
            </span>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={clearFilters}
                className="border-slate-200 text-[#7f8c8d] hover:border-[#b8924f] hover:text-[#b8924f]"
              >
                {t.clear_filters}
              </Button>
              <Button
                onClick={applyFilters}
                className="bg-gradient-to-r from-[#b8924f] to-[#d4af6a] hover:from-[#a07f42] hover:to-[#c9a05d] text-white px-8"
              >
                {t.apply_filters}
              </Button>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <p className="text-sm text-[#7f8c8d] mb-6">
          {t.showing_results
            .replace('{count}', visibleListings.length.toString())
            .replace('{total}', filteredListings.length.toString())}
        </p>

        {/* Content Wrapper */}
        <div className="pb-10">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-[#6b6b6b] text-2xl md:text-3xl font-light tracking-[8px] mb-2">
              ACQUIZGROUP
            </h2>
            <p className="text-[#6b6b6b] text-sm tracking-[4px] uppercase">{t.tagline}</p>
          </div>

          {/* Listings Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
            {visibleListings.map((listing) => (
              <div
                key={listing.id}
                className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 flex flex-col cursor-pointer"
                onClick={() => listing.detailUrl && navigate(listing.detailUrl)}
              >
                {/* Image Section */}
                <div
                  className="relative h-52 bg-cover bg-center"
                  style={{ backgroundImage: `url(${listing.imageUrl})` }}
                >
                  {/* Image Count */}
                  <div className="absolute top-3 left-3 bg-black/70 text-white px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5">
                    <Camera className="w-4 h-4" />
                    {listing.imageCount}
                  </div>

                  {/* Country Badge */}
                  <div className="absolute top-3 right-3 flex flex-col gap-1.5 items-end">
                    <div className="bg-gradient-to-r from-[#b8924f] to-[#d4af6a] text-white px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-wide">
                      {getCountryBadge(listing.location.country)}
                    </div>
                    {pendingDealIds.has(listing.id) && (
                      <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {language === 'en' ? 'Pending' : 'En attente'}
                      </div>
                    )}
                  </div>

                  {/* Favorite Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleFavorite(listing);
                    }}
                    className={`absolute bottom-3 right-3 w-11 h-11 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 ${
                      isListingFavorite(listing)
                        ? 'bg-gradient-to-r from-[#b8924f] to-[#d4af6a]'
                        : 'bg-white'
                    }`}
                  >
                    <Heart
                      className={`w-5 h-5 ${
                        isListingFavorite(listing) ? 'text-white fill-white' : 'text-[#7f8c8d]'
                      }`}
                    />
                  </button>
                </div>

                {/* Content Section */}
                <div className="p-6 flex-1 flex flex-col">
                  {/* Listing Number */}
                  <div className="text-xs text-[#b8924f] font-semibold mb-2">
                    {t.listing_number}{listing.listingNumber}
                  </div>

                  {/* Header */}
                  <div className="flex justify-between items-start gap-4 mb-4">
                    <h3 className="text-lg font-semibold text-[#2c3e50] leading-tight flex-1">
                      {getListingTitle(listing.id)}
                    </h3>
                    <div className="text-right shrink-0">
                      <div className="text-2xl font-bold text-[#1a1a1a]">
                        {formatPrice(listing.financials.price, listing.financials.currency)}
                      </div>
                      <div className="text-sm text-[#6b6b6b]">
                        {t.cash_flow}: <span className="font-semibold text-[#2c3e50]">{formatCashFlow(listing.financials.cashFlow)}</span>
                      </div>
                      <div className="text-xs text-[#7f8c8d]">
                        <span className="font-semibold text-[#6b6b6b]">{t.sde}:</span> {formatCashFlow(listing.financials.sde)}
                      </div>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="flex items-center gap-1.5 text-sm text-[#7f8c8d] mb-3">
                    <MapPin className="w-4 h-4" />
                    {listing.location.city}, {listing.location.state}
                  </div>

                  {/* Description */}
                  <p className="text-sm text-[#555] leading-relaxed mb-5 flex-1">
                    {getListingDescription(listing.id)}
                  </p>

                  {/* Footer */}
                  <div className="border-t border-slate-100 pt-4 flex justify-between items-center flex-wrap gap-3">
                    <div className="flex gap-2 flex-wrap">
                      {listing.badges.map((badge, idx) => {
                        const getBadgeStyle = () => {
                          switch (badge.type) {
                            case 'seller_financing':
                              return 'bg-gradient-to-r from-emerald-500 to-emerald-400 text-white';
                            case 'e2_visa':
                              return 'bg-gradient-to-r from-blue-500 to-blue-400 text-white';
                            case 'absentee_owner':
                              return 'bg-gradient-to-r from-purple-500 to-purple-400 text-white';
                            case 'semi_absentee':
                              return 'bg-gradient-to-r from-violet-500 to-violet-400 text-white';
                            case 'home_based':
                              return 'bg-gradient-to-r from-orange-500 to-orange-400 text-white';
                            default:
                              return 'bg-slate-100 text-[#6b6b6b]';
                          }
                        };

                        const getBadgeLabel = () => {
                          switch (badge.type) {
                            case 'seller_financing':
                              return t.seller_financing;
                            case 'e2_visa':
                              return `✓ ${t.e2_visa}`;
                            case 'absentee_owner':
                              return t.special_absentee_owner;
                            case 'semi_absentee':
                              return t.special_semi_absentee;
                            case 'home_based':
                              return t.special_home_based;
                            default:
                              return badge.label;
                          }
                        };

                        return (
                          <span
                            key={idx}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wide ${getBadgeStyle()}`}
                          >
                            {getBadgeLabel()}
                          </span>
                        );
                      })}
                    </div>
                    {listing.preFinancingOffers > 0 && (
                      <div className="bg-gradient-to-r from-[#b8924f] to-[#d4af6a] text-white px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-1.5">
                        ✓ {listing.preFinancingOffers} {t.pre_financing_offers}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Infinite Scroll Trigger */}
          {hasMore && (
            <div ref={loadMoreRef} className="flex justify-center py-8">
              {isLoadingMore && (
                <div className="flex items-center gap-2 text-[#7f8c8d]">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>{language === 'en' ? 'Loading more...' : 'Chargement...'}</span>
                </div>
              )}
            </div>
          )}

          {filteredListings.length === 0 && (
            <div className="text-center py-16">
              <p className="text-[#7f8c8d] text-lg">
                {language === 'en' ? 'No listings match your criteria.' : 'Aucune annonce ne correspond à vos critères.'}
              </p>
            </div>
          )}
        </div>
      </div>
      <ScrollToTop />
    </div>
  );
}
